import {
  useMutation,
  useQuery,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { z } from 'zod';
import { AuditLogEntrySchema, type AuditLogEntry } from '@datazo/contracts';
import { apiFetch } from '@/shared/api/client';

const AuditListResponseSchema = z.object({
  items: z.array(AuditLogEntrySchema),
  page: z.number().int().min(1),
  pageSize: z.number().int().min(1),
  total: z.number().int().min(0),
});

export type AuditListResponse = z.infer<typeof AuditListResponseSchema>;

export interface AuditListParams {
  page: number;
  pageSize: number;
  usuarioId?: string;
  modulo?: string;
  /** Filtro exacto por `entidad` — útil para timeline de un episodio. */
  entidad?: string;
  desde?: string;
  hasta?: string;
}

export const auditKeys = {
  all: ['audit'] as const,
  list: (params: AuditListParams) => ['audit', 'list', params] as const,
};

export function useAuditList(
  params: AuditListParams,
): UseQueryResult<AuditListResponse, Error> {
  return useQuery({
    queryKey: auditKeys.list(params),
    queryFn: async () => {
      const qs = new URLSearchParams({
        page: String(params.page),
        pageSize: String(params.pageSize),
      });
      if (params.usuarioId !== undefined && params.usuarioId !== '') {
        qs.set('usuarioId', params.usuarioId);
      }
      if (params.modulo !== undefined && params.modulo !== '') {
        qs.set('modulo', params.modulo);
      }
      if (params.entidad !== undefined && params.entidad !== '') {
        qs.set('entidad', params.entidad);
      }
      if (params.desde !== undefined && params.desde !== '') {
        qs.set('desde', params.desde);
      }
      if (params.hasta !== undefined && params.hasta !== '') {
        qs.set('hasta', params.hasta);
      }
      return apiFetch<AuditListResponse>(`/api/audit?${qs.toString()}`, {
        parseAs: AuditListResponseSchema,
      });
    },
    staleTime: 30_000,
    placeholderData: (previous) => previous,
  });
}

/**
 * Resultado del endpoint `GET /api/audit/verify`. Si la cadena está intacta
 * devuelve `{ ok: true }`. Si detecta tampering, devuelve el id del registro
 * donde se rompe la integridad — útil para forense.
 */
const VerifyChainResponseSchema = z.discriminatedUnion('ok', [
  z.object({ ok: z.literal(true) }),
  z.object({ ok: z.literal(false), brokenAt: z.string() }),
]);

export type VerifyChainResult = z.infer<typeof VerifyChainResponseSchema>;

/**
 * Verifica la integridad de la cadena de auditoría. La exponemos como
 * `useMutation` (no `useQuery`) porque es una acción que el usuario
 * dispara explícitamente desde un botón — no debe ejecutarse en cada
 * render ni cachearse.
 */
export function useVerifyAuditChain(): UseMutationResult<VerifyChainResult, Error, void> {
  return useMutation({
    mutationFn: async () =>
      apiFetch<VerifyChainResult>('/api/audit/verify', {
        parseAs: VerifyChainResponseSchema,
      }),
  });
}

export type { AuditLogEntry };
